import type { MapFeature } from "../redux/global-map.types"
import { EntityIcon } from "./EntityIcon.tsx"

interface MarkerHoverTooltipProps {
  feature: Pick<
    MapFeature,
    "entity_type" | "reference" | "title" | "customer_name" | "address"
  >
}

/**
 * A small, presentational hover card for a map marker — glyph + reference +
 * title + customer name, nothing else. Deliberately NOT `EntityQuickView`:
 * no tabs, no actions, no close button, no data fetch of its own — it only
 * reads the fields already on the marker's own `MapFeature` (Task 19's
 * `reference`/`customer_name`), so a user panning across a dense cluster of
 * markers never fires a burst of hover-triggered requests. Positioning is
 * the caller's job (`MapCanvas` wraps this in an absolutely-positioned box
 * near the hovered marker's screen coordinates) — this component only knows
 * how to render its own content.
 */
const MarkerHoverTooltip = ({ feature }: MarkerHoverTooltipProps) => {
  return (
    <div
      role="tooltip"
      data-testid="marker-hover-tooltip"
      className="pointer-events-none flex max-w-[260px] items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
    >
      <EntityIcon entityType={feature.entity_type} className="size-6" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[11px] text-gray-500">
          {feature.reference}
        </span>
        <span className="truncate font-semibold text-gray-900">
          {feature.title}
        </span>
        <span className="truncate text-gray-500">
          {feature.customer_name ?? "—"}
        </span>
        <span className="truncate text-gray-500">{feature.address ?? "—"}</span>
      </span>
    </div>
  )
}

export default MarkerHoverTooltip
export type { MarkerHoverTooltipProps }
